import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { LanguageSelector } from "../components/LanguageSelector";
import { 
  Leaf, 
  Target, 
  BarChart3, 
  Shield, 
  Award,
  ArrowRight,
  CheckCircle,
  Users,
  TrendingUp,
  FileCheck,
  Newspaper,
  Building2,
  Zap,
  Globe,
  Calendar,
  Landmark,
  Activity,
  GraduationCap,
  Brain,
  Lock
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

export default function Landing() {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: Target,
      title: t('landing.featuresSection.aiQualification'),
      description: t('landing.featuresSection.aiQualificationDesc')
    },
    {
      icon: Leaf,
      title: t('landing.featuresSection.esgScore'),
      description: t('landing.featuresSection.esgScoreDesc')
    },
    {
      icon: Shield,
      title: t('landing.featuresSection.csrdCompliance'),
      description: t('landing.featuresSection.csrdComplianceDesc')
    },
    {
      icon: Award,
      title: t('landing.featuresSection.loyalty'),
      description: t('landing.featuresSection.loyaltyDesc')
    },
    {
      icon: Newspaper,
      title: t('landing.featuresSection.news'),
      description: t('landing.featuresSection.newsDesc')
    },
    {
      icon: BarChart3,
      title: t('landing.featuresSection.analytics'),
      description: t('landing.featuresSection.analyticsDesc')
    }
  ];

  const stats = [
    { value: t('landing.stats.decisions'), label: t('landing.stats.decisionsLabel') },
    { value: t('landing.stats.companies'), label: t('landing.stats.companiesLabel') },
    { value: t('landing.stats.satisfaction'), label: t('landing.stats.satisfactionLabel') },
    { value: t('landing.stats.support'), label: t('landing.stats.supportLabel') }
  ];

  const certifications = [
    { icon: Globe, text: t('landing.certifications.rgpd') },
    { icon: FileCheck, text: t('landing.certifications.iso') },
    { icon: Target, text: t('landing.certifications.esrs') }
  ];

  // Demo data for analytics charts
  const performanceData = [
    { month: 'Jan', environnement: 65, social: 58, gouvernance: 72 },
    { month: 'Fév', environnement: 68, social: 62, gouvernance: 74 },
    { month: 'Mar', environnement: 72, social: 65, gouvernance: 76 },
    { month: 'Avr', environnement: 75, social: 70, gouvernance: 78 },
    { month: 'Mai', environnement: 78, social: 74, gouvernance: 80 },
    { month: 'Juin', environnement: 82, social: 78, gouvernance: 83 },
  ];

  const decisionTypes = [
    { name: 'Stratégique', value: 35, color: '#22c55e' },
    { name: 'Tactique', value: 45, color: '#3b82f6' },
    { name: 'Opérationnelle', value: 20, color: '#f59e0b' },
  ];

  const weeklyActivity = [
    { day: 'Lun', decisions: 12 },
    { day: 'Mar', decisions: 19 },
    { day: 'Mer', decisions: 15 },
    { day: 'Jeu', decisions: 22 },
    { day: 'Ven', decisions: 18 },
    { day: 'Sam', decisions: 8 },
    { day: 'Dim', decisions: 5 },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_429cff5b-3177-447c-8270-ff638670e578/artifacts/n5bgru61_LOGO.png" 
                alt="VF Decide" 
                className="h-10 w-auto"
              />
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-1 text-sm">
            <Link to="/vf-actions" className="px-4 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              {t('nav.vfActions')}
            </Link>
            <Link to="/temoignages" className="px-4 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              {t('nav.useCases')}
            </Link>
            <Link to="/pricing" className="px-4 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              {t('nav.pricing')}
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="nav-login-btn">{t('nav.login')}</Button>
            </Link>
            <Link to="/register">
              <Button size="sm" className="rounded-md" data-testid="nav-register-btn">
                {t('nav.start')}
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6 animate-fadeIn">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <FileCheck className="w-4 h-4" />
                {t('landing.badge')}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight">
                {t('landing.title')}
                <span className="text-primary"> {t('landing.titleHighlight')}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                {t('landing.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="rounded-md px-6" data-testid="hero-cta-btn">
                    <Zap className="w-5 h-5 mr-2" />
                    {t('landing.cta')}
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="rounded-md px-6" data-testid="hero-demo-btn">
                    <Calendar className="w-5 h-5 mr-2" />
                    {t('landing.ctaDemo')}
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 pt-2">
                {[t('landing.features.esrs'), t('landing.features.doubleMateriality'), t('landing.features.scope')].map((tag) => (
                  <span key={tag} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-primary" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="animate-fadeIn stagger-2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/c9494d0d-73ab-44f8-9e0a-94701bb6dc88/images/31d18d267fd607eebcd67724b675974fc63620df07c8091032fecd29b6e3c686.png"
                alt="Sustainable business"
                className="rounded-lg shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="stat-value text-primary mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {t('landing.featuresSection.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.featuresSection.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="p-6 space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section - Credibility */}
      <section className="py-20 px-6 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              {t('landing.methodology.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.methodology.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('landing.methodology.standards')}</h3>
              <p className="text-sm text-muted-foreground">{t('landing.methodology.standardsDesc')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Brain className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('landing.methodology.ai')}</h3>
              <p className="text-sm text-muted-foreground">{t('landing.methodology.aiDesc')}</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Lock className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('landing.methodology.security')}</h3>
              <p className="text-sm text-muted-foreground">{t('landing.methodology.securityDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* For Everyone Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-600 text-sm font-medium">
                <GraduationCap className="w-4 h-4" />
                {t('landing.forEveryone.badge')}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                {t('landing.forEveryone.title')}
                <span className="text-blue-600"> {t('landing.forEveryone.titleHighlight')}</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                {t('landing.forEveryone.description')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Users, text: t('landing.forEveryone.feature1') },
                  { icon: Target, text: t('landing.forEveryone.feature2') },
                  { icon: Award, text: t('landing.forEveryone.feature3') },
                  { icon: TrendingUp, text: t('landing.forEveryone.feature4') }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 text-foreground">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-blue-500" />
                    </div>
                    <span className="font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
              <Link to="/register">
                <Button size="lg" className="rounded-full bg-blue-500 hover:bg-blue-600">
                  {t('landing.forEveryone.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-3xl blur-3xl" />
              <img 
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt={t('landing.forEveryone.imageAlt')}
                className="relative rounded-3xl shadow-xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
              {t('landing.howItWorks.title')}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: t('landing.howItWorks.step1'), desc: t('landing.howItWorks.step1Desc'), icon: FileCheck },
              { step: "02", title: t('landing.howItWorks.step2'), desc: t('landing.howItWorks.step2Desc'), icon: BarChart3 },
              { step: "03", title: t('landing.howItWorks.step3'), desc: t('landing.howItWorks.step3Desc'), icon: TrendingUp }
            ].map((item, index) => (
              <div key={index} className="text-center space-y-4 animate-fadeIn" style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="w-16 h-16 mx-auto rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                  {item.title}
                </h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Analytics Dashboard Preview */}
      <section className="py-24 px-6 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 text-primary text-sm font-medium mb-6">
              <Activity className="w-4 h-4" />
              {t('landing.analytics.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
              {t('landing.analytics.title')}
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              {t('landing.analytics.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Chart - ESG Performance */}
            <Card className="lg:col-span-2 bg-slate-800/50 border-slate-700 backdrop-blur">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
                      {t('landing.analytics.esgPerformance')}
                    </h3>
                    <p className="text-sm text-slate-400">{t('landing.analytics.evolution')}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <span className="text-slate-400">{t('esg.environment')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-slate-400">{t('esg.social')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span className="text-slate-400">{t('esg.governance')}</span>
                    </div>
                  </div>
                </div>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={performanceData}>
                      <defs>
                        <linearGradient id="colorEnv" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorSoc" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorGov" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} domain={[50, 100]} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px',
                          color: '#fff'
                        }}
                      />
                      <Area type="monotone" dataKey="environnement" stroke="#22c55e" fill="url(#colorEnv)" strokeWidth={2} />
                      <Area type="monotone" dataKey="social" stroke="#3b82f6" fill="url(#colorSoc)" strokeWidth={2} />
                      <Area type="monotone" dataKey="gouvernance" stroke="#f59e0b" fill="url(#colorGov)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Right Column - Stats */}
            <div className="space-y-6">
              {/* Decision Types Pie */}
              <Card className="bg-slate-800/50 border-slate-700 backdrop-blur">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                    {t('landing.analytics.decisionTypes')}
                  </h3>
                  <div className="h-[160px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={decisionTypes}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={60}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {decisionTypes.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1e293b', 
                            border: '1px solid #334155',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-4 mt-2">
                    {decisionTypes.map((type, index) => (
                      <div key={index} className="flex items-center gap-1 text-xs">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }}></div>
                        <span className="text-slate-400">{type.name}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Activity */}
              <Card className="bg-slate-800/50 border-slate-700 backdrop-blur">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
                    {t('landing.analytics.weeklyActivity')}
                  </h3>
                  <div className="h-[120px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={weeklyActivity}>
                        <XAxis dataKey="day" stroke="#64748b" fontSize={10} />
                        <YAxis stroke="#64748b" fontSize={10} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#1e293b', 
                            border: '1px solid #334155',
                            borderRadius: '8px',
                            color: '#fff'
                          }}
                        />
                        <Bar dataKey="decisions" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { value: "E-S-G", label: t('landing.analytics.avgScore'), trend: "Score détaillé" },
              { value: "IA", label: t('landing.analytics.decisionsAnalyzed'), trend: "Analyse auto" },
              { value: "CSRD", label: t('landing.analytics.csrdCompliance'), trend: "Conforme 2026" },
              { value: "PDF", label: t('landing.analytics.roi'), trend: "Export rapide" }
            ].map((stat, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 backdrop-blur">
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                    {stat.value}
                  </div>
                  <div className="text-xs text-slate-400 mb-1">{stat.label}</div>
                  <div className="text-xs text-emerald-400 font-medium">{stat.trend}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/register">
              <Button size="lg" className="rounded-full px-8 bg-primary hover:bg-primary/90">
                <BarChart3 className="w-5 h-5 mr-2" />
                {t('landing.analytics.accessDashboard')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            {t('landing.cta2.title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('landing.cta2.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="rounded-full px-12 text-lg" data-testid="cta-register-btn">
                {t('landing.cta2.button')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button size="lg" variant="outline" className="rounded-full px-12 text-lg">
                <Calendar className="w-5 h-5 mr-2" />
                {t('landing.ctaDemo')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-card border-t border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_429cff5b-3177-447c-8270-ff638670e578/artifacts/n5bgru61_LOGO.png" 
                  alt="VF Decide" 
                  className="h-9 w-auto"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                {t('landing.footer.description')}
              </p>
            </div>

            {/* Product */}
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">{t('landing.footer.product')}</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/pricing" className="hover:text-primary transition-colors">{t('nav.pricing')}</Link>
                <Link to="/demo" className="hover:text-primary transition-colors">{t('nav.demo')}</Link>
                <Link to="/temoignages" className="hover:text-primary transition-colors">{t('nav.useCases')}</Link>
                <Link to="/vf-actions" className="hover:text-primary transition-colors">{t('nav.vfActions')}</Link>
                <Link to="/actualites" className="hover:text-primary transition-colors">{t('nav.news')}</Link>
              </div>
            </div>

            {/* Resources */}
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">{t('landing.footer.resources')}</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/register" className="hover:text-primary transition-colors">{t('landing.footer.createAccount')}</Link>
                <Link to="/login" className="hover:text-primary transition-colors">{t('landing.footer.signIn')}</Link>
                <a href="https://vf-dashboard.emergent.host" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">VF Dashboard</a>
                <a href="https://carbon-impact-4.emergent.host" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Carbon Impact</a>
              </div>
            </div>

            {/* Legal */}
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">{t('landing.footer.legal')}</h4>
              <div className="flex flex-col gap-2 text-sm text-muted-foreground">
                <Link to="/mentions-legales" className="hover:text-primary transition-colors">{t('landing.footer.legalNotice')}</Link>
                <Link to="/cgu" className="hover:text-primary transition-colors">{t('landing.footer.terms')}</Link>
                <Link to="/confidentialite" className="hover:text-primary transition-colors">{t('landing.footer.privacy')}</Link>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              {t('landing.footer.copyright')}
            </p>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{t('landing.footer.rgpdCompliant')}</span>
              <span>•</span>
              <span>{t('landing.footer.iso')}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
